/**
 * Staff Payroll Register — PDF Export (client-side, jsPDF + autotable)
 *
 * Consumes the same computed row data as the XLSX export (served by the
 * /staff-register-data/ endpoint) so both formats are identical.
 *
 * Cover ..... Full department-level payroll summary and signatories
 * Page 2+ ... Per-department employee tables (all XLSX columns, no signatures)
 */

import toast from '../utils/toast';
import {
  ALIGN,
  DEFAULT_TABLE_STYLES,
  createBrandedPDF,
  drawSeparatorLine,
  addBrandedFooter,
  fmtCurrency,
  fmtDate,
} from './pdfReportTemplate';
import {
  buildPayrollCoverSummary,
  drawPayrollRegisterCoverPage,
} from './payrollRegisterCoverPage';

const C = ALIGN.CURRENCY;
const N = ALIGN.NUMBER;
const T = ALIGN.TEXT;

// ---------------------------------------------------------------------------
// Schema-driven column config — derived from the backend COLUMN_SCHEMA served
// via /staff-register-data/.  Do NOT add columns here; edit
// staff_payroll_register_export_service.py's COLUMN_SCHEMA instead.
// ---------------------------------------------------------------------------

const TYPE_CONFIG = {
  text:     { halign: T, fmt: null },
  currency: { halign: C, fmt: fmtCurrency },
  number:   { halign: N, fmt: fmtNum },
  integer:  { halign: N, fmt: null },
  rate:     { halign: N, fmt: fmtRate },
};

function buildPdfColumns(schema) {
  return schema.map((col) => {
    const tc = TYPE_CONFIG[col.type] ?? TYPE_CONFIG.text;
    return {
      key:    col.key,
      label:  col.label,
      band:   col.band,
      halign: tc.halign,
      fmt:    tc.fmt,
      ...(col.pdf_width != null ? { width: col.pdf_width } : {}),
    };
  });
}

// Colour palettes — keyed by band name from the schema
const BAND_STYLES = {
  cpf: {
    headerRGB:  [30, 90, 138],
    rowOddRGB:  [232, 244, 252],
    rowEvenRGB: [204, 229, 246],
    totalRGB:   [22, 66, 102],
  },
  ded: {
    headerRGB:  [139, 69, 83],
    rowOddRGB:  [254, 245, 247],
    rowEvenRGB: [250, 220, 226],
    totalRGB:   [100, 45, 60],
  },
};

function num(val) {
  if (val == null || val === '') return 0;
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return isNaN(n) ? 0 : n;
}

function fmtNum(val) {
  const n = num(val);
  return n === 0 ? '-' : n.toFixed(2);
}

function fmtRate(val) {
  const n = num(val);
  if (n === 0) return '-';
  return `${(n * 100).toFixed(1)}%`;
}

function groupByDept(rows) {
  const groups = {};
  for (const row of rows) {
    const dept = row.department || 'Unassigned';
    if (!groups[dept]) groups[dept] = [];
    groups[dept].push(row);
  }
  return groups;
}

function rowToArray(columns, row, idx) {
  return columns.map((col) => {
    if (col.key === 'sn') return idx;
    const raw = row[col.key];
    if (raw == null) return '-';
    if (col.fmt) return col.fmt(raw);
    if (typeof raw === 'number') return raw === 0 ? '-' : raw;
    return raw;
  });
}

function sumField(rows, key) {
  return rows.reduce((s, r) => s + num(r[key]), 0);
}

/**
 * Export the Staff Payroll Register as a branded PDF.
 *
 * @param {Array}  rows       - Pre-computed row dicts from /staff-register-data/
 * @param {Object} payrollRun - { period_name, period_start_date, period_end_date, status }
 * @param {Array}  columns    - Column schema from /staff-register-data/ response.columns
 */
export async function exportStaffRegisterPDF(rows, payrollRun, columns, coverSummary = null) {
  if (!rows?.length) {
    toast.error('No staff payroll data to export');
    return;
  }

  const groups = groupByDept(rows);
  const deptOrder = Object.keys(groups).sort();

  const periodLabel = payrollRun?.period_name
    || (payrollRun?.period_start_date
      ? `${fmtDate(payrollRun.period_start_date)} — ${fmtDate(payrollRun.period_end_date)}`
      : 'Unknown Period');

  const layout = await createBrandedPDF({
    title: 'Staff Payroll Register',
    subtitle: `Period: ${periodLabel}`,
    orientation: 'landscape',
  });
  const { doc, autoTable: autoTableFn } = layout;

  // ── COVER PAGE — approval summary and sole signatory block ──────────

  const coverIdentityKeys = new Set([
    'sn',
    'staff_id',
    'name',
    'department',
    'designation',
    'pass_type',
  ]);
  const coverMetrics = columns
    .filter((column) => !coverIdentityKeys.has(column.key))
    .map((column) => ({
      key: column.key,
      label: column.label,
      sourceKey: column.key,
      type: column.type,
      band: column.band,
    }));
  const resolvedCoverSummary = coverSummary || buildPayrollCoverSummary(rows, {
    groupKey: 'department',
    groupLabel: 'Department',
    payableKey: 'final_payment',
    metrics: coverMetrics,
    groupOrder: deptOrder,
    groupLabels: Object.fromEntries(deptOrder.map((dept) => [dept, dept.slice(0, 31)])),
  });
  await drawPayrollRegisterCoverPage({
    doc,
    autoTableFn,
    layout,
    summary: resolvedCoverSummary,
    registerTitle: 'Staff Payroll Register',
    periodLabel,
    statusLabel: payrollRun?.status,
  });

  // ── PAGE 2+ — Per-department detail tables ──────────────────────────

  const deptPdfColumns = buildPdfColumns(columns);

  const colStyles = {};
  deptPdfColumns.forEach((col, i) => {
    colStyles[i] = { halign: col.halign };
    if (col.width) colStyles[i].cellWidth = col.width;
  });

  const colBandByIdx = new Map(deptPdfColumns.map((col, i) => [i, col.band ?? null]));

  for (const deptName of deptOrder) {
    const deptRows = groups[deptName];
    if (deptRows.length === 0) continue;

    doc.addPage('landscape');
    let deptY = 14;

    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(27, 42, 74);
    doc.text(deptName, layout.leftX, deptY);

    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(120);
    doc.text(`${deptRows.length} employee${deptRows.length !== 1 ? 's' : ''}  •  ${periodLabel}`, layout.leftX, deptY + 5);
    doc.setTextColor(0);
    deptY += 9;
    drawSeparatorLine(doc, deptY, layout.leftX, layout.rightX);

    const bodyData = deptRows.map((r, i) => rowToArray(deptPdfColumns, r, i + 1));

    const totalRow = deptPdfColumns.map((col, ci) => {
      if (ci === 0) return { content: `Total — ${deptRows.length}`, colSpan: 6, styles: { fontStyle: 'bold', halign: 'right' } };
      if (ci >= 1 && ci <= 5) return undefined;
      const val = sumField(deptRows, col.key);
      const formatted = col.fmt ? col.fmt(val) : (val === 0 ? '-' : val);
      return { content: formatted, styles: { fontStyle: 'bold' } };
    }).filter(v => v !== undefined);

    bodyData.push(totalRow);
    const totalIdx = bodyData.length - 1;

    autoTableFn(doc, {
      startY: deptY,
      head: [deptPdfColumns.map((c) => c.label)],
      body: bodyData,
      ...DEFAULT_TABLE_STYLES,
      styles: { ...DEFAULT_TABLE_STYLES.styles, fontSize: 5.5, cellPadding: 1 },
      headStyles: { ...DEFAULT_TABLE_STYLES.headStyles, fontSize: 5.5 },
      margin: layout.tableMargin,
      columnStyles: colStyles,
      didParseCell: (data) => {
        const band = colBandByIdx.get(data.column.index);
        const bs = band ? BAND_STYLES[band] : null;

        if (data.section === 'head') {
          if (bs) data.cell.styles.fillColor = bs.headerRGB;
          return;
        }

        if (data.section !== 'body') return;

        if (data.row.index === totalIdx) {
          data.cell.styles.fillColor = bs ? bs.totalRGB : [31, 41, 55];
          data.cell.styles.textColor = 255;
          data.cell.styles.fontStyle = 'bold';
        } else if (bs) {
          data.cell.styles.fillColor = data.row.index % 2 === 0 ? bs.rowOddRGB : bs.rowEvenRGB;
        } else if (data.row.index % 2 === 1) {
          data.cell.styles.fillColor = [249, 250, 251];
        }
      },
    });

  }

  addBrandedFooter(doc, layout);

  const periodSlug = (payrollRun?.period_name || 'Unknown')
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '');
  doc.save(`Staff_Payroll_Register_${periodSlug}.pdf`);
  toast.success('Staff Payroll Register PDF exported');
}
